import sortBy from "lodash/sortBy";
import { FC, Fragment, JSX, useEffect, useRef, useState } from "react";
import { TemplateSpecialtyGroup } from "@app/templates";
import { SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { difference, isEqual } from "lodash";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";

interface SpecialtyGroupsProps {
  canEdit: boolean;
  defaultOpen?: boolean;
  excludedSpecialtyGroupCodes: string[];
  disagreedExcludedSpecialtyGroupCodes?: string[];
  disagreedTelehealthSpecialtyGroupCodes?: string[];
  handleSpecialtyGroupsChange: (selectedRecords: TemplateSpecialtyGroup[]) => void;
  handleTelehealthSpecialtyGroupsChange: (selectedSpecialtyGroupCodes: string[]) => void;
  telehealthSpecialtyGroupCodes: string[];
  specialtyGroups: TemplateSpecialtyGroup[];
}

interface TableData {
  code: string;
  codeDisplay: JSX.Element;
  key: string;
  name: string;
  nameDisplay: JSX.Element;
  telehealthCheckbox: JSX.Element;
}

const SpecialtyGroupsTable: FC<SpecialtyGroupsProps> = (props: SpecialtyGroupsProps) => {
  const sortOrder = useRef<Array<keyof TemplateSpecialtyGroup>>(["displayOrder"]);
  const initialSpecialtyGroups = useRef(props.specialtyGroups);
  const basePanelTitle = "Included Specialty Groups";

  const [panelTitle, setPanelTitle] = useState(basePanelTitle);
  const [sortedRecords, setSortedRecords] = useState(props.specialtyGroups && sortBy(props.specialtyGroups, sortOrder.current) || props.specialtyGroups);

  useEffect(() => {
    if (!isEqual(props.specialtyGroups, initialSpecialtyGroups.current)) {
      setPanelTitle(`${basePanelTitle} (new)`);
      setSortedRecords(props.specialtyGroups && sortBy(props.specialtyGroups, sortOrder.current) || props.specialtyGroups);
    }
  }, [props.specialtyGroups]);

  if (!sortedRecords) {
    return null;
  }

  const records: TableData[] = sortedRecords.map((rec) => {
    const telehealthCheckbox = (rec.hasTelehealthAccessPercentageCredits
      ? (
        <QESCheckbox
          label=""
          testId={`specialty-groups-table__telehealth--${rec.code}`}
          checked={props.telehealthSpecialtyGroupCodes?.includes(rec.code) && !props.disagreedTelehealthSpecialtyGroupCodes?.includes(rec.code)}
          disabled={!props.canEdit}
          onChange={() => handleTelehealthSpecialtyGroupChange(rec.code)}
          isError={props.disagreedTelehealthSpecialtyGroupCodes?.includes(rec.code)}
          indeterminate={props.disagreedTelehealthSpecialtyGroupCodes?.includes(rec.code)}
        />
      )
      : <></>);

    return {
      code: rec.code,
      codeDisplay: (<div className={props.disagreedExcludedSpecialtyGroupCodes?.includes(rec.code) ? "text-danger" : ""}>{rec.code}</div>),
      name: rec.name,
      nameDisplay: (<div className={props.disagreedExcludedSpecialtyGroupCodes?.includes(rec.code) ? "text-danger" : ""}>{rec.name}</div>),
      telehealthCheckbox,
      [wafflesKeyColumnName]: rec.code,
    };
  });

  const handleBulkTelehealthChange = () => {
    const rowsWithTelehealth = sortedRecords.filter((sr) => !!sr.hasTelehealthAccessPercentageCredits);
    if (props.telehealthSpecialtyGroupCodes.length === rowsWithTelehealth.length) {
      props.handleTelehealthSpecialtyGroupsChange([]);
    } else {
      props.handleTelehealthSpecialtyGroupsChange(rowsWithTelehealth.map((r) => r.code));
      const currentSelectedSpecialtyGroupCodes = props.specialtyGroups.filter(s => !props.excludedSpecialtyGroupCodes.includes(s.code));
      const newSelectedSpecialtyGroupCodes = difference(rowsWithTelehealth, currentSelectedSpecialtyGroupCodes).filter(s => !props.telehealthSpecialtyGroupCodes.includes(s.code));
      props.handleSpecialtyGroupsChange(currentSelectedSpecialtyGroupCodes.concat(newSelectedSpecialtyGroupCodes));
    }
  }

  const rowsWithTelehealth = sortedRecords.filter((sr) => !!sr.hasTelehealthAccessPercentageCredits);

  const columnConfig = {
    codeDisplay: {
      displayText: "Code",
      testId: "code",
    },
    nameDisplay: {
      displayText: "Description",
      testId: "description",
    },
    telehealthCheckbox: {
      displayElement: (
        <span>
          <QESCheckbox
            label="Telehealth"
            className="waffles-head-checkbox"
            checked={props.telehealthSpecialtyGroupCodes?.length === rowsWithTelehealth.length}
            disabled={!props.canEdit}
            onChange={handleBulkTelehealthChange}
            onClick={(e) => e.stopPropagation()}
            indeterminate={props.telehealthSpecialtyGroupCodes && props.telehealthSpecialtyGroupCodes.length > 0 && props.telehealthSpecialtyGroupCodes.length < rowsWithTelehealth.length}
            testId="specialty-groups-table__bulk-telehealth"
          />
        </span>
      ),
      displayText: "Telehealth",
      hidden: sortedRecords.every((sr) => !sr.hasTelehealthAccessPercentageCredits),
      noSort: true,
      testId: "telehealth",
    },
  };

  const handleWaffleSelectionChange = (selectedCodes: string[]) => {
    const selectedProps = sortedRecords.filter((sr) => {
      return selectedCodes.includes(sr.code);
    });

    const unchecked = difference(props.specialtyGroups.filter((sr) => !!sr.hasTelehealthAccessPercentageCredits), selectedProps.filter((sr) => !!sr.hasTelehealthAccessPercentageCredits))?.map(sgc => sgc.code);
    const newTelehealth = props?.telehealthSpecialtyGroupCodes.filter(t => !unchecked.includes(t)) || [];
    props.handleTelehealthSpecialtyGroupsChange(newTelehealth);

    props.handleSpecialtyGroupsChange(selectedProps);
  }

  const handleTelehealthSpecialtyGroupChange = (selectedSpecialtyGroupCode: string) => {
    const selectedCodes = props.telehealthSpecialtyGroupCodes.includes(selectedSpecialtyGroupCode)
      ? props.telehealthSpecialtyGroupCodes.filter((sgc) => sgc !== selectedSpecialtyGroupCode)
      : props.telehealthSpecialtyGroupCodes.concat([selectedSpecialtyGroupCode]);

    const newExcludedSpecialtyGroupCodes = props.excludedSpecialtyGroupCodes.filter(e => selectedSpecialtyGroupCode !== e);
    const newSelectedSpecialtyGroupCode = props.specialtyGroups.filter(s => !newExcludedSpecialtyGroupCodes.includes(s.code));
    props.handleSpecialtyGroupsChange(newSelectedSpecialtyGroupCode);

    props.handleTelehealthSpecialtyGroupsChange(selectedCodes);
  }

  const customSort = (records: TableData[], column: keyof TableData, sortDirection: SortDirections) => {
    const multiplier = sortDirection === SortDirections.Asc ? 1 : -1;

    switch (column) {
      case "codeDisplay":
        return [...records].sort((a, b) => a.code.localeCompare(b.code) * multiplier);
      case "nameDisplay":
        return [...records].sort((a, b) => a.name.localeCompare(b.name) * multiplier);
      default:
        return records;
    }
  };

  return (
    <div className="form-group">
      <div>
        <AccordionContainer>
          <Accordion
            title={panelTitle}
            defaultOpen={!!props.defaultOpen}
            testId={`specialty-groups-table__${panelTitle}-accordion`}
          >
            <Fragment>
              {(props.canEdit || null) &&
                <div className="mb15">
                  Check the specialty groups to include...
                </div>
              }
              <Waffles<TableData>
                columns={columnConfig}
                customSort={customSort}
                data={records}
                handleCheckboxChange={handleWaffleSelectionChange}
                selectedKeys={records.filter((r) => !props.excludedSpecialtyGroupCodes?.includes(r.code)).map((m) => m.key)}
                showCheckboxes={props.canEdit}
                testId="specialty-groups-table"
                indeterminateKeys={records.filter((r) =>
                  !!props.disagreedExcludedSpecialtyGroupCodes &&
                  props.disagreedExcludedSpecialtyGroupCodes?.includes(r.code)
                ).map((m) => m.key)}
              />
            </Fragment>
          </Accordion>
        </AccordionContainer>
      </div>
    </div>
  );
}

export default SpecialtyGroupsTable;